/**
 * Inspector Roadmap tool (#280).
 *
 * Reads and mutates brunel.roadmap_items — the same store Main Command's
 * get_roadmap tool projects. Not a second list.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Project, RoadmapItemRow } from '../lib/data';
import { useRoadmapMutation } from '../lib/mutations';
import {
  ROADMAP_HORIZONS,
  ROADMAP_STATUSES,
  bumpRoadmapRank,
  filterRoadmapItems,
  groupRoadmapItems,
  nextRoadmapRank,
  normalizeRoadmapSummary,
  normalizeRoadmapTitle,
  type RoadmapHorizon,
  type RoadmapItem,
  type RoadmapStatus,
} from '../lib/roadmap';

function toItem(row: RoadmapItemRow, projects: readonly Project[]): RoadmapItem {
  const project = row.project_id ? projects.find((p) => p.id === row.project_id) : null;
  return {
    org_id: row.org_id,
    id: row.id,
    title: row.title,
    summary: row.summary,
    horizon: row.horizon,
    status: row.status,
    rank: row.rank,
    project_id: row.project_id,
    project_name: project?.name ?? null,
    owner_label: row.owner_label,
    source_command_id: row.source_command_id,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function RoadmapPanel({
  client,
  orgId,
  projects,
  items,
  focusItemId = null,
  onWorkItem,
  onChanged,
}: {
  readonly client: SupabaseClient;
  readonly orgId: string;
  readonly projects: readonly Project[];
  readonly items: readonly RoadmapItemRow[];
  /** Opens the exact item when Work Board routes the owner back to the canonical Roadmap. */
  readonly focusItemId?: string | null;
  /** Stages this existing item into Main Command; it never creates a second task or roadmap row. */
  readonly onWorkItem?: (item: RoadmapItem) => void;
  readonly onChanged: () => void;
}) {
  const { upsert, busy, error } = useRoadmapMutation(client);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<RoadmapStatus | 'all'>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<RoadmapItem | null>(null);
  const [adding, setAdding] = useState(false);

  const mapped = useMemo(() => items.map((row) => toItem(row, projects)), [items, projects]);
  const filtered = useMemo(
    () => filterRoadmapItems(mapped, query, statusFilter),
    [mapped, query, statusFilter],
  );
  const groups = useMemo(() => groupRoadmapItems(filtered), [filtered]);

  useEffect(() => {
    if (focusItemId === null || !mapped.some((item) => item.id === focusItemId)) return;
    setQuery('');
    setStatusFilter('all');
    setAdding(false);
    setEditing(null);
    setExpandedId(focusItemId);
  }, [focusItemId, mapped]);

  const save = useCallback(async (draft: {
    id?: string;
    title: string;
    summary: string;
    horizon: RoadmapHorizon;
    status: RoadmapStatus;
    rank: number;
    projectId: string | null;
    ownerLabel: string | null;
  }) => {
    const title = normalizeRoadmapTitle(draft.title);
    if (title === '') return;
    const id = await upsert({
      orgId,
      id: draft.id,
      title,
      summary: normalizeRoadmapSummary(draft.summary),
      horizon: draft.horizon,
      status: draft.status,
      rank: draft.rank,
      projectId: draft.projectId,
      ownerLabel: draft.ownerLabel,
    });
    if (id) {
      setAdding(false);
      setEditing(null);
      setExpandedId(id);
      onChanged();
    }
  }, [orgId, upsert, onChanged]);

  return (
    <div className="roadmap-panel stack" data-testid="roadmap-panel">
      <div className="row spread wrap roadmap-toolbar">
        <input
          className="roadmap-search"
          type="search"
          placeholder="Search roadmap…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search roadmap"
        />
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as RoadmapStatus | 'all')}
        >
          <option value="all">All statuses</option>
          {ROADMAP_STATUSES.map((status) => (
            <option key={status.id} value={status.id}>{status.label}</option>
          ))}
        </select>
        <button type="button" className="primary" onClick={() => { setAdding(true); setEditing(null); }}>
          New item
        </button>
      </div>

      {error && <p className="error" role="alert">{error}</p>}

      {adding && (
        <section className="roadmap-new-item" aria-label="New roadmap item">
          <h3>New roadmap item</h3>
          <RoadmapEditor
            projects={projects}
            initial={{
              title: '',
              summary: '',
              horizon: 'next',
              status: 'open',
              rank: nextRoadmapRank(mapped, 'next'),
              projectId: null,
              ownerLabel: null,
            }}
            busy={busy}
            onCancel={() => setAdding(false)}
            onSave={(draft) => { void save(draft); }}
          />
        </section>
      )}

      {ROADMAP_HORIZONS.map((horizon) => {
        const rows = groups[horizon.id];
        return (
          <section key={horizon.id} className="roadmap-group" aria-label={horizon.label}>
            <header className="row spread">
              <h3>{horizon.label}</h3>
              <small className="muted">{rows.length}</small>
            </header>
            {rows.length === 0 ? (
              <p className="muted roadmap-empty">Nothing in {horizon.label.toLowerCase()}.</p>
            ) : (
              <ul className="roadmap-list">
                {rows.map((item) => {
                  const open = expandedId === item.id;
                  return (
                    <li key={item.id} className={open ? 'roadmap-item open' : 'roadmap-item'}>
                      <button
                        type="button"
                        className="roadmap-item-main"
                        aria-expanded={open}
                        onClick={() => setExpandedId(open ? null : item.id)}
                      >
                        <span className="roadmap-item-title">{item.title}</span>
                        <span className="roadmap-item-meta muted">
                          #{item.rank} · {statusLabel(item.status)}
                          {item.project_name ? ` · ${item.project_name}` : ''}
                          {item.owner_label ? ` · ${item.owner_label}` : ''}
                        </span>
                      </button>
                      {open && editing?.id !== item.id && (
                        <div className="roadmap-item-detail stack">
                          {item.summary ? <p>{item.summary}</p> : null}
                          <p className="muted">
                            Updated {formatWhen(item.updated_at)}
                            {item.created_at !== item.updated_at ? ` · created ${formatWhen(item.created_at)}` : ''}
                          </p>
                          <div className="row wrap roadmap-item-actions">
                            {onWorkItem !== undefined && item.status !== 'done' && item.status !== 'dropped' && (
                              <button type="button" className="primary" disabled={busy} onClick={() => onWorkItem(item)}>
                                {item.project_name ? 'Work on this project' : 'Plan this work'}
                              </button>
                            )}
                            {item.status !== 'done' && item.status !== 'dropped' && (
                              <button type="button" className="ghost" disabled={busy} onClick={() => {
                                void save({
                                  id: item.id, title: item.title, summary: item.summary,
                                  horizon: item.horizon, status: 'done', rank: item.rank,
                                  projectId: item.project_id, ownerLabel: item.owner_label,
                                });
                              }}>
                                Mark done
                              </button>
                            )}
                            {item.status !== 'dropped' && (
                              <button type="button" className="ghost" disabled={busy} onClick={() => {
                                void save({
                                  id: item.id, title: item.title, summary: item.summary,
                                  horizon: item.horizon, status: 'dropped', rank: item.rank,
                                  projectId: item.project_id, ownerLabel: item.owner_label,
                                });
                              }}>
                                Drop
                              </button>
                            )}
                            <label>
                              Status
                              <select
                                value={item.status}
                                disabled={busy}
                                onChange={(event) => {
                                  void save({
                                    id: item.id,
                                    title: item.title,
                                    summary: item.summary,
                                    horizon: item.horizon,
                                    status: event.target.value as RoadmapStatus,
                                    rank: item.rank,
                                    projectId: item.project_id,
                                    ownerLabel: item.owner_label,
                                  });
                                }}
                              >
                                {ROADMAP_STATUSES.map((status) => (
                                  <option key={status.id} value={status.id}>{status.label}</option>
                                ))}
                              </select>
                            </label>
                            <label>
                              Horizon
                              <select
                                value={item.horizon}
                                disabled={busy}
                                onChange={(event) => {
                                  void save({
                                    id: item.id,
                                    title: item.title,
                                    summary: item.summary,
                                    horizon: event.target.value as RoadmapHorizon,
                                    status: item.status,
                                    rank: item.rank,
                                    projectId: item.project_id,
                                    ownerLabel: item.owner_label,
                                  });
                                }}
                              >
                                {ROADMAP_HORIZONS.map((entry) => (
                                  <option key={entry.id} value={entry.id}>{entry.label}</option>
                                ))}
                              </select>
                            </label>
                            <button
                              type="button"
                              className="ghost"
                              disabled={busy}
                              onClick={() => {
                                void save({
                                  id: item.id,
                                  title: item.title,
                                  summary: item.summary,
                                  horizon: item.horizon,
                                  status: item.status,
                                  rank: bumpRoadmapRank(item.rank, 'up'),
                                  projectId: item.project_id,
                                  ownerLabel: item.owner_label,
                                });
                              }}
                            >
                              Higher
                            </button>
                            <button
                              type="button"
                              className="ghost"
                              disabled={busy}
                              onClick={() => {
                                void save({
                                  id: item.id,
                                  title: item.title,
                                  summary: item.summary,
                                  horizon: item.horizon,
                                  status: item.status,
                                  rank: bumpRoadmapRank(item.rank, 'down'),
                                  projectId: item.project_id,
                                  ownerLabel: item.owner_label,
                                });
                              }}
                            >
                              Lower
                            </button>
                            <button type="button" className="ghost" onClick={() => setEditing(item)}>Edit</button>
                          </div>
                        </div>
                      )}
                      {editing?.id === item.id && (
                        <RoadmapEditor
                          projects={projects}
                          initial={{
                            title: item.title,
                            summary: item.summary,
                            horizon: item.horizon,
                            status: item.status,
                            rank: item.rank,
                            projectId: item.project_id,
                            ownerLabel: item.owner_label,
                          }}
                          busy={busy}
                          onCancel={() => setEditing(null)}
                          onSave={(draft) => { void save({ ...draft, id: item.id }); }}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

function RoadmapEditor({
  projects,
  initial,
  busy,
  onCancel,
  onSave,
}: {
  readonly projects: readonly Project[];
  readonly initial: {
    title: string;
    summary: string;
    horizon: RoadmapHorizon;
    status: RoadmapStatus;
    rank: number;
    projectId: string | null;
    ownerLabel: string | null;
  };
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSave: (draft: {
    title: string;
    summary: string;
    horizon: RoadmapHorizon;
    status: RoadmapStatus;
    rank: number;
    projectId: string | null;
    ownerLabel: string | null;
  }) => void;
}) {
  const [title, setTitle] = useState(initial.title);
  const [summary, setSummary] = useState(initial.summary);
  const [horizon, setHorizon] = useState<RoadmapHorizon>(initial.horizon);
  const [status, setStatus] = useState<RoadmapStatus>(initial.status);
  const [rank, setRank] = useState(initial.rank);
  const [projectId, setProjectId] = useState(initial.projectId ?? '');
  const [ownerLabel, setOwnerLabel] = useState(initial.ownerLabel ?? '');

  useEffect(() => {
    setTitle(initial.title);
    setSummary(initial.summary);
    setHorizon(initial.horizon);
    setStatus(initial.status);
    setRank(initial.rank);
    setProjectId(initial.projectId ?? '');
    setOwnerLabel(initial.ownerLabel ?? '');
  }, [initial]);

  return (
    <form
      className="roadmap-editor stack"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          title,
          summary,
          horizon,
          status,
          rank,
          projectId: projectId === '' ? null : projectId,
          ownerLabel: ownerLabel.trim() === '' ? null : ownerLabel.trim(),
        });
      }}
    >
      <label>
        Title
        <input value={title} onChange={(event) => setTitle(event.target.value)} required maxLength={200} autoFocus />
      </label>
      <label>
        Summary
        <textarea value={summary} onChange={(event) => setSummary(event.target.value)} rows={3} maxLength={2000} />
      </label>
      <div className="row wrap">
        <label>
          Horizon
          <select value={horizon} onChange={(event) => setHorizon(event.target.value as RoadmapHorizon)}>
            {ROADMAP_HORIZONS.map((entry) => (
              <option key={entry.id} value={entry.id}>{entry.label}</option>
            ))}
          </select>
        </label>
        <label>
          Status
          <select value={status} onChange={(event) => setStatus(event.target.value as RoadmapStatus)}>
            {ROADMAP_STATUSES.map((entry) => (
              <option key={entry.id} value={entry.id}>{entry.label}</option>
            ))}
          </select>
        </label>
        <label>
          Rank
          <input type="number" min={0} max={1000000} value={rank} onChange={(event) => setRank(Number(event.target.value) || 0)} />
        </label>
      </div>
      <div className="row wrap">
        <label>
          App / project
          <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">None</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </label>
        <label>
          Owner / agent
          <input value={ownerLabel} onChange={(event) => setOwnerLabel(event.target.value)} maxLength={120} placeholder="optional" />
        </label>
      </div>
      <div className="row wrap">
        <button type="button" className="ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className="primary" disabled={busy || normalizeRoadmapTitle(title) === ''}>Save</button>
      </div>
    </form>
  );
}

function statusLabel(status: RoadmapStatus): string {
  return ROADMAP_STATUSES.find((entry) => entry.id === status)?.label ?? status;
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
